import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  getProfileListing,
  getUserById,
  getPreferences,
  getUnlockedProfiles,
} from "../utils/api";

function calculateMatch(profile, prefs) {
  if (!prefs) return 0;
  const checks = [];
  if (prefs.ageRange?.min != null && prefs.ageRange?.max != null && profile.dob) {
    const age = new Date().getFullYear() - new Date(profile.dob).getFullYear();
    checks.push(age >= prefs.ageRange.min && age <= prefs.ageRange.max);
  }
  if (prefs.lookingFor) {
    const wantMale = prefs.lookingFor.toLowerCase() === "groom";
    checks.push(
      (wantMale && profile.gender === "Male") || (!wantMale && profile.gender === "Female")
    );
  }
  if (prefs.motherTongue?.length) {
    checks.push(prefs.motherTongue.includes(profile.motherTongue));
  }
  if (prefs.education?.length && profile.education) {
    const edus = Array.isArray(profile.education)
      ? profile.education
      : [profile.education];
    checks.push(edus.some((e) => prefs.education.includes(e)));
  }
  ["country", "state", "city"].forEach((loc) => {
    if (prefs[loc]) checks.push(profile[loc] === prefs[loc]);
  });
  const total =
    (prefs.ageRange ? 1 : 0) +
    (prefs.lookingFor ? 1 : 0) +
    (prefs.motherTongue?.length ? 1 : 0) +
    (prefs.education?.length ? 1 : 0) +
    (prefs.country ? 1 : 0) +
    (prefs.state ? 1 : 0) +
    (prefs.city ? 1 : 0);
  const matched = checks.filter(Boolean).length;
  return total > 0 ? Math.round((matched / total) * 100) : 0;
}

const ProfileCard = ({ profile, unlockedIds }) => {
  const navigate = useNavigate();
  const age = profile.dob
    ? new Date().getFullYear() - new Date(profile.dob).getFullYear()
    : "—";
  const isPrivate = profile.visibility === "Private" && !unlockedIds.includes(profile._id);

  return (
    <div className={`bg-white rounded-3xl overflow-hidden shadow-lg relative ${
      isPrivate ? "pointer-events-none" : ""
    }`}>
      {/* Match badge */}
      {profile.matchPercent != null && (
        <div className="absolute top-2 left-2 bg-white bg-opacity-80 px-2 py-1 text-xs font-semibold rounded">
          {profile.matchPercent}% Match
        </div>
      )}

      {/* Entrepreneur tag */}
      {profile.tag && (
        <span className="absolute top-2 right-2 bg-yellow-300 px-3 py-1 text-sm rounded-full">
          {profile.tag}
        </span>
      )}

      <img
        src={profile.images?.[0] || profile.image || "/fallback.jpg"}
        alt={profile.fullName || profile.name || "Profile Image"}
        className={`w-full h-[300px] object-cover ${
          isPrivate ? "filter blur-sm" : ""
        }`}
        onError={(e) => (e.target.src = "/fallback.jpg")}
      />

      {isPrivate && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <span className="text-white text-lg font-semibold">
            Private Profile
          </span>
        </div>
      )}

      <div className="bg-gradient-to-t from-black to-transparent text-white p-4 absolute bottom-0 left-0 right-0">
        <h3 className="text-lg font-bold mb-1">
          {profile.fullName || profile.name || "—"}
        </h3>
        <div className="text-xs grid grid-cols-2 gap-2 px-2 py-1">
          <span className="flex items-center gap-1">
            <img src="/icon1.png" alt="age" className="w-4 h-4" />
            {age} Years
          </span>
          <span className="flex items-center gap-1">
            <img src="/icon2.png" alt="height" className="w-4 h-4" />
            {profile.height || "—"}
          </span>
          <span className="flex items-center gap-1">
            <img src="/icon3.png" alt="location" className="w-4 h-4" />
            {profile.address || profile.cityName || "—"}
          </span>
          <span className="flex items-center gap-1">
            <img src="/icon4.png" alt="gender" className="w-4 h-4" />
            {profile.gender || "—"}
          </span>
        </div>
        <button
          className="mt-2 px-4 py-1.5 bg-gradient-to-r from-[#E7603C] to-[#B9309A] rounded-full text-xs disabled:opacity-50"
          onClick={() => navigate(`/matrimony/profile-details/${profile._id}`)}
          disabled={isPrivate}
        >
          View Profile
        </button>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="bg-gray-200 rounded-3xl animate-pulse h-[420px] min-w-[280px]" />
);

export default function ProfileGrid({
  profiles: propProfiles = null,
  title = "Recent Uploads",
  viewAllLink = "/matrimony/profiles/recent",
}) {
  const ENTREPRENEUR_OCCUPATION_ID = "68515ca576ce3b5d818b5b08";
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasFounder, setHasFounder] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [unlockedIds, setUnlockedIds] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const arr = await getPreferences();
        setPreferences(arr[0] || null);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const uid = localStorage.getItem("matrimonyUserId");
      if (!uid) return setHasFounder(false);
      try {
        const user = await getUserById(uid);
        setHasFounder(!!user.founder_id?.founderId);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getUnlockedProfiles();
        const unlockedProfileIds = res.data.map((entry) => entry.profile._id);
        setUnlockedIds(unlockedProfileIds);
      } catch (e) {
        console.error("Failed to fetch unlocked profiles", e);
      }
    })();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let list = propProfiles;
      if (!list) {
        try {
          list = await getProfileListing();
        } catch {
          list = [];
        }
      }
      if (!hasFounder) {
        list = list.filter((p) => p.occupation === ENTREPRENEUR_OCCUPATION_ID);
      }
      const annotated = list.map((p) => ({
        ...p,
        matchPercent: calculateMatch(p, preferences),
        tag:
          p.occupation === ENTREPRENEUR_OCCUPATION_ID
            ? "Entrepreneur"
            : undefined,
      }));
      setProfiles(annotated);
      setLoading(false);
    };
    load();
  }, [propProfiles, hasFounder, preferences]);

  return (
    <div className="py-10 px-4 container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">
          {title.split(" ")[0]}{" "}
          <span className="bg-gradient-to-r from-[#E25B46] via-[#B9398E] to-[#E25B46] bg-clip-text text-transparent">
            {title.split(" ").slice(1).join(" ")}
          </span>
        </h2>
        <button
          className="text-sm text-[#E25B46] hover:text-[#B9398E] flex items-center gap-1"
          onClick={() => navigate(viewAllLink)}
        >
          View All →
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          No profiles found.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {profiles.map((profile) => (
            <ProfileCard key={profile._id} profile={profile} unlockedIds={unlockedIds} />
          ))}
        </div>
      )}

      {loading && (
        <div className="flex justify-center mt-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#E7603C]" />
        </div>
      )}
    </div>
  );
}